import { BufferAttribute, type BufferGeometry, type Mesh, type Object3D, type SkinnedMesh, Vector3 } from 'three';
import { LoopSubdivision } from 'three-subdivide';
import { smoothShade } from './smooth';

/** Zabezpieczenie: duże modele (np. krypta) nie są dzielone dalej — i tak są gładkie, a kosztowałyby pamięć. */
const MAX_TRIANGLES = 12000;

/**
 * Wygładza bryły low-poly podziałem Loopa: każdy trójkąt dzieli się na cztery, a wierzchołki
 * przesuwają się w stronę gładkiej powierzchni. Graniaste stożki drzew i kanciaste kamienie
 * stają się miękkie i zaokrąglone, jak w ręcznie modelowanych grafikach. Na koniec gładkie normalne.
 */
export function subdivideModel(root: Object3D, options: { iterations?: number; skinnedOnly?: boolean } = {}): void {
  const iterations = options.iterations ?? 1;
  const done = new Map<BufferGeometry, BufferGeometry>();
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (!mesh.isMesh) return;
    // Dla postaci dzielimy tylko ciało — broń i dodatki zostają ostre.
    if (options.skinnedOnly && !(mesh as SkinnedMesh).isSkinnedMesh) return;
    const cached = done.get(mesh.geometry);
    if (cached) {
      mesh.geometry = cached;
      return;
    }
    const before = mesh.geometry;
    const after = (mesh as SkinnedMesh).isSkinnedMesh ? subdivideSkinned(before, iterations) : subdivide(before, iterations);
    done.set(before, after);
    mesh.geometry = after;
    if (options.skinnedOnly) smoothShade(mesh, 80);
  });
  if (!options.skinnedOnly) smoothShade(root, 80);
}

export function subdivide(g: BufferGeometry, iterations: number): BufferGeometry {
  return LoopSubdivision.modify(g, iterations, { split: true, uvSmooth: false, preserveEdges: false, flatOnly: false, maxTriangles: MAX_TRIANGLES });
}

/**
 * Siatka ze szkieletem: kości i wagi nie mogą być uśredniane (numer kości „2,5” nie istnieje),
 * więc po podziale każdy nowy wierzchołek bierze kości z najbliższego oryginalnego wierzchołka.
 */
export function subdivideSkinned(g: BufferGeometry, iterations: number): BufferGeometry {
  const srcPos = g.getAttribute('position') as BufferAttribute;
  const srcIdx = g.getAttribute('skinIndex') as BufferAttribute | undefined;
  const srcW = g.getAttribute('skinWeight') as BufferAttribute | undefined;
  const out = subdivide(g, iterations);
  if (!srcIdx || !srcW) return out;

  // Siatka przestrzenna oryginalnych wierzchołków do szybkiego szukania najbliższego.
  g.computeBoundingBox();
  const size = g.boundingBox!.getSize(new Vector3()).length();
  const cell = size / 40;
  const grid = new Map<string, number[]>();
  const key = (x: number, y: number, z: number) => `${Math.floor(x / cell)},${Math.floor(y / cell)},${Math.floor(z / cell)}`;
  for (let i = 0; i < srcPos.count; i++) {
    const k = key(srcPos.getX(i), srcPos.getY(i), srcPos.getZ(i));
    const list = grid.get(k);
    if (list) list.push(i);
    else grid.set(k, [i]);
  }

  const pos = out.getAttribute('position') as BufferAttribute;
  const skinIndex = new Uint16Array(pos.count * 4);
  const skinWeight = new Float32Array(pos.count * 4);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const cx = Math.floor(x / cell);
    const cy = Math.floor(y / cell);
    const cz = Math.floor(z / cell);
    let best = -1;
    let bestD = Infinity;
    for (let r = 1; r <= 3 && best < 0; r++) {
      for (let dx = -r; dx <= r; dx++) {
        for (let dy = -r; dy <= r; dy++) {
          for (let dz = -r; dz <= r; dz++) {
            for (const j of grid.get(`${cx + dx},${cy + dy},${cz + dz}`) ?? []) {
              const d = (srcPos.getX(j) - x) ** 2 + (srcPos.getY(j) - y) ** 2 + (srcPos.getZ(j) - z) ** 2;
              if (d < bestD) {
                bestD = d;
                best = j;
              }
            }
          }
        }
      }
    }
    if (best < 0) best = 0;
    for (let k = 0; k < 4; k++) {
      skinIndex[i * 4 + k] = srcIdx.getComponent(best, k);
      skinWeight[i * 4 + k] = srcW.getComponent(best, k);
    }
  }
  out.setAttribute('skinIndex', new BufferAttribute(skinIndex, 4));
  out.setAttribute('skinWeight', new BufferAttribute(skinWeight, 4));
  return out;
}
